import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Snackbar, Alert } from '@mui/material';
import {
  addNode, addEdge, updateNode, updateEdge, removeNode, removeEdge, setNodePositions,
} from '../features/networkUndoSlice';
import {
  setSelectedNode, setSelectedEdge, setSelection, clearSelection, setSelfReference, requestFit,
} from '../features/networkSlice';
import {
  idKey, sameId, isSelfLoop, nextId, POSITIVE, NEGATIVE, DEFAULT_LOOP_ANGLE,
} from '../lib/model';
import { nodeSize, boundsOf } from '../lib/geometry/nodes';
import {
  DISPLAY_ID, DISPLAY_LABEL, presentNode, presentShape, defaultShapeForMode,
  isIdMode, resizeNode, storedSizeFromDisplay,
} from '../lib/display';
import {
  edgeGeometry, connectionGeometry, offsetForPair, roundnessFromDrag, loopAngleFromDrag,
  PAIR_OFFSET,
} from '../lib/geometry/edges';
import {
  SNAP_SIZE, CLICK_TOLERANCE, POSITION_SAVE_DELAY_MS, PHYSICS_DEFAULTS,
} from '../lib/network/constants';
import useViewport from './Network/useViewport';
import useForceLayout from './Network/useForceLayout';
import useImageSizes from './Network/useImageSizes';
import NodeView from './Network/NodeView';
import EdgeView from './Network/EdgeView';
import ConnectionLine from './Network/ConnectionLine';
import Grid from './Network/Grid';
import EditNodeDialog from './EditNodeDialog';
import EditEdgeDialog from './EditEdgeDialog';

const EDGE_TOOLS = { edgePos: POSITIVE, edgeNeg: NEGATIVE };
const SNACKBAR_MS = 2000;
const EMPTY = [];

const snap = (value, enabled) => (enabled ? Math.round(value / SNAP_SIZE) * SNAP_SIZE : value);

/** What is under a pointer: a node, a resize handle, a link handle, a link, or the empty canvas. */
const findTarget = (element) => {
  const resizeEl = element?.closest?.('[data-resize-dir]');
  if (resizeEl) {
    const nodeEl = resizeEl.closest('[data-node-id]');
    if (nodeEl) {
      return {
        type: 'resize',
        key: nodeEl.getAttribute('data-node-id'),
        dir: resizeEl.getAttribute('data-resize-dir'),
      };
    }
  }
  const handleEl = element?.closest?.('[data-edge-handle]');
  if (handleEl) return { type: 'handle', key: handleEl.getAttribute('data-edge-handle') };
  const nodeEl = element?.closest?.('[data-node-id]');
  if (nodeEl) return { type: 'node', key: nodeEl.getAttribute('data-node-id') };
  const edgeEl = element?.closest?.('[data-edge-id]');
  if (edgeEl) return { type: 'edge', key: edgeEl.getAttribute('data-edge-id') };
  return { type: 'background' };
};

const sameTarget = (a, b) => a?.type === b?.type && a?.key === b?.key;
const elementAt = (x, y, fallback) => (
  typeof document.elementFromPoint === 'function' ? document.elementFromPoint(x, y) : fallback
);
const distance = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
const midpoint = (a, b) => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });

export default function NetworkPanel() {
  const dispatch = useDispatch();

  // global state
  const tool = useSelector((state) => state.toolbar.tool);
  const nodes = useSelector((state) => state.networkUndo.present.nodes);
  const edges = useSelector((state) => state.networkUndo.present.edges);
  const physics = useSelector((state) => state.network.physics);
  const physicsSettingsFromState = useSelector((state) => state.network.physicsSettings);
  const selfReference = useSelector((state) => state.network.selfReference);
  const snapToGrid = useSelector((state) => state.network.snapToGrid);
  const offsetPairs = useSelector((state) => state.network.offsetPairs !== false);
  const pairOffset = useSelector((state) => (
    Number.isFinite(state.network.pairOffset) ? state.network.pairOffset : PAIR_OFFSET
  ));
  const displayMode = useSelector((state) => (
    state.network.displayMode === DISPLAY_ID ? DISPLAY_ID : DISPLAY_LABEL
  ));
  const defaultNodeShape = useSelector((state) => (
    defaultShapeForMode(displayMode, state.network.defaultNodeShape)
  ));
  const highlightedNodes = useSelector((state) => state.network.highlightedNodes) ?? EMPTY;
  const highlightedEdges = useSelector((state) => state.network.highlightedEdges) ?? EMPTY;
  const fitRequest = useSelector((state) => state.network.fitRequest);

  const physicsSettings = useMemo(
    () => ({ ...PHYSICS_DEFAULTS, ...(physicsSettingsFromState || {}) }),
    [physicsSettingsFromState],
  );

  // local state
  const [livePositions, setLivePositions] = useState({}); // key -> { x, y } while dragging / simulating
  const [liveSizes, setLiveSizes] = useState({}); // key -> { x, y, width, height } while resizing
  const [liveRoundness, setLiveRoundness] = useState({}); // key -> roundness while dragging a handle
  const [liveLoopAngle, setLiveLoopAngle] = useState({}); // key -> loopAngle while dragging a self-effect handle
  const [hovered, setHovered] = useState(null);
  const [connection, setConnection] = useState(null); // { fromKey, x, y } while dragging a new link
  const [saveSnackbar, setSaveSnackbar] = useState(false);

  const svgRef = useRef(null);
  const gestureRef = useRef(null);
  const pointersRef = useRef(new Map());
  const saveTimerRef = useRef(null);
  const snackbarTimerRef = useRef(null);

  // latest values for pointer/keyboard handlers
  const latest = useRef({});
  latest.current = {
    tool, nodes, edges, physics, snapToGrid, offsetPairs, pairOffset, displayMode, defaultNodeShape, highlightedNodes, highlightedEdges, livePositions, liveSizes, liveRoundness, liveLoopAngle,
  };

  const {
    transform, transformRef, updateTransform, toWorld, fitBounds, getRect, clampScale,
  } = useViewport(svgRef);

  // sizes (image nodes depend on the image's aspect ratio)
  const imageSizes = useImageSizes(nodes);
  const sizeOf = useCallback((node) => {
    const view = presentNode(node, displayMode);
    return nodeSize(view, view.shape === 'image' ? imageSizes[node.image] : null);
  }, [imageSizes, displayMode]);

  // nodes with live positions / sizes, drawn for the current display mode
  const positioned = useMemo(() => nodes.map((node) => {
    const key = idKey(node.id);
    const live = livePositions[key];
    const sized = liveSizes[key];
    let next = node;
    if (live) next = { ...next, x: live.x, y: live.y };
    if (sized) {
      next = {
        ...next, x: sized.x, y: sized.y, width: sized.width, height: sized.height, resized: true,
      };
    }
    return presentNode(next, displayMode);
  }), [nodes, livePositions, liveSizes, displayMode]);
  const nodesByKey = useMemo(() => new Map(positioned.map((node) => [idKey(node.id), node])), [positioned]);
  const sizesByKey = useMemo(
    () => new Map(positioned.map((node) => [idKey(node.id), sizeOf(node)])),
    [positioned, sizeOf],
  );
  latest.current.nodesByKey = nodesByKey;
  latest.current.sizesByKey = sizesByKey;

  const visibleEdges = useMemo(() => edges.filter((edge) => (
    (selfReference || !isSelfLoop(edge))
    && nodesByKey.has(idKey(edge.from))
    && nodesByKey.has(idKey(edge.to))
  )), [edges, selfReference, nodesByKey]);

  const edgeGeometries = useMemo(() => visibleEdges.map((edge) => {
    const key = idKey(edge.id);
    const roundness = Object.prototype.hasOwnProperty.call(liveRoundness, key)
      ? liveRoundness[key]
      : edge.roundness;
    const loopAngle = Object.prototype.hasOwnProperty.call(liveLoopAngle, key)
      ? liveLoopAngle[key]
      : edge.loopAngle;
    const liveEdge = {
      ...edge, roundness, loopAngle,
    };
    return {
      edge: liveEdge,
      geometry: edgeGeometry({
        edge: liveEdge,
        from: nodesByKey.get(idKey(edge.from)),
        fromSize: sizesByKey.get(idKey(edge.from)),
        to: nodesByKey.get(idKey(edge.to)),
        toSize: sizesByKey.get(idKey(edge.to)),
        offset: offsetPairs ? offsetForPair(edge, visibleEdges, pairOffset) : 0,
      }),
    };
  }), [visibleEdges, nodesByKey, sizesByKey, liveRoundness, liveLoopAngle, offsetPairs, pairOffset]);

  const connectionShape = useMemo(() => {
    if (!connection) return null;
    const from = nodesByKey.get(connection.fromKey);
    if (!from) return null;
    return connectionGeometry({
      from,
      fromSize: sizesByKey.get(connection.fromKey),
      to: { x: connection.x, y: connection.y },
      sign: EDGE_TOOLS[tool] || POSITIVE,
    });
  }, [connection, nodesByKey, sizesByKey, tool]);

  // ---- physics -------------------------------------------------------------------------------

  const clearTimers = useCallback(() => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    if (snackbarTimerRef.current) clearTimeout(snackbarTimerRef.current);
    saveTimerRef.current = null;
    snackbarTimerRef.current = null;
  }, []);

  const layoutRef = useRef(null);
  const handleTick = useCallback((positions) => setLivePositions(positions), []);
  const handleEnd = useCallback(() => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      saveTimerRef.current = null;
      setSaveSnackbar(true);
      dispatch(setNodePositions(layoutRef.current.getPositions()));
      snackbarTimerRef.current = setTimeout(() => setSaveSnackbar(false), SNACKBAR_MS);
    }, POSITION_SAVE_DELAY_MS);
  }, [dispatch]);

  const layout = useForceLayout({
    enabled: physics,
    nodes,
    edges,
    sizeOf,
    settings: physicsSettings,
    onTick: handleTick,
    onEnd: handleEnd,
  });
  layoutRef.current = layout;

  // keep the layout the simulation reached when it is switched off
  useEffect(() => {
    if (physics) return;
    clearTimers();
    setSaveSnackbar(false);
    const live = latest.current.livePositions;
    if (Object.keys(live).length > 0) {
      dispatch(setNodePositions(live));
      setLivePositions({});
    }
  }, [physics, dispatch, clearTimers]);

  useEffect(() => () => clearTimers(), [clearTimers]);

  // ---- viewport ------------------------------------------------------------------------------

  const fit = useCallback(() => {
    const { nodesByKey: byKey } = latest.current;
    fitBounds(boundsOf([...byKey.values()], sizeOf));
  }, [fitBounds, sizeOf]);

  useEffect(() => {
    fit();
    // only when explicitly requested (and once on mount)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fitRequest]);

  // ---- editing helpers -----------------------------------------------------------------------

  const nodeByKey = (key) => latest.current.nodes.find((node) => sameId(node.id, key));
  const edgeByKey = (key) => latest.current.edges.find((edge) => sameId(edge.id, key));

  const selectTarget = useCallback((target) => {
    if (target.type === 'node') {
      const node = nodeByKey(target.key);
      if (!node) return;
      const connected = latest.current.edges
        .filter((edge) => sameId(edge.from, node.id) || sameId(edge.to, node.id))
        .map((edge) => edge.id);
      dispatch(setSelection({ nodes: [node.id], edges: connected }));
    } else if (target.type === 'edge') {
      const edge = edgeByKey(target.key);
      if (edge) dispatch(setSelection({ nodes: [], edges: [edge.id] }));
    } else {
      dispatch(clearSelection());
    }
  }, [dispatch]);

  const openEditor = useCallback((target) => {
    if (target.type === 'node') {
      const node = nodeByKey(target.key);
      if (node) dispatch(setSelectedNode(node));
    } else if (target.type === 'edge') {
      const edge = edgeByKey(target.key);
      if (edge) dispatch(setSelectedEdge(edge));
    }
  }, [dispatch]);

  const deleteTarget = useCallback((target) => {
    if (target.type === 'node') {
      const node = nodeByKey(target.key);
      if (node) dispatch(removeNode(node.id));
    } else if (target.type === 'edge') {
      const edge = edgeByKey(target.key);
      if (edge) dispatch(removeEdge(edge.id));
    }
    dispatch(clearSelection());
  }, [dispatch]);

  const createNode = useCallback((world) => {
    const { nodes: current, snapToGrid: snapping, defaultNodeShape: shape } = latest.current;
    const id = nextId(current);
    const node = {
      id,
      label: String(id),
      shape,
      x: snap(world.x, snapping),
      y: snap(world.y, snapping),
    };
    dispatch(addNode(node));
  }, [dispatch]);

  const createEdge = useCallback((fromKey, toKey) => {
    const sign = EDGE_TOOLS[latest.current.tool];
    const from = nodeByKey(fromKey);
    const to = nodeByKey(toKey);
    if (!sign || !from || !to) return;
    if (sameId(from.id, to.id)) dispatch(setSelfReference(true));
    const existing = latest.current.edges.find(
      (edge) => sameId(edge.from, from.id) && sameId(edge.to, to.id),
    );
    if (existing) {
      if (existing.sign !== sign) dispatch(updateEdge({ id: existing.id, sign }));
      return;
    }
    dispatch(addEdge({
      id: nextId(latest.current.edges),
      from: from.id,
      to: to.id,
      sign,
      roundness: 0,
      loopAngle: DEFAULT_LOOP_ANGLE,
    }));
  }, [dispatch]);

  const handleClick = useCallback((target, world) => {
    const { tool: currentTool } = latest.current;
    if (currentTool === 'newNode') {
      createNode(world);
    } else if (currentTool === 'delete') {
      deleteTarget(target);
    } else {
      selectTarget(target);
    }
  }, [createNode, deleteTarget, selectTarget]);

  // ---- pointer gestures ----------------------------------------------------------------------

  const onPointerDown = (event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    const svg = svgRef.current;
    pointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (svg.setPointerCapture) svg.setPointerCapture(event.pointerId);

    if (pointersRef.current.size === 2) {
      const points = [...pointersRef.current.values()];
      setConnection(null);
      gestureRef.current = {
        kind: 'pinch',
        startDistance: distance(points[0], points[1]),
        startMid: midpoint(points[0], points[1]),
        startTransform: { ...transformRef.current },
      };
      return;
    }

    let target = findTarget(event.target);
    const start = { x: event.clientX, y: event.clientY };
    if (target.type === 'resize') {
      const node = latest.current.nodesByKey.get(target.key);
      const size = latest.current.sizesByKey?.get(target.key);
      gestureRef.current = {
        kind: 'resize',
        key: target.key,
        dir: target.dir,
        start,
        origin: node && size ? {
          x: node.x, y: node.y, width: size.width, height: size.height,
        } : null,
        originShape: node?.shape,
        moved: false,
      };
      return;
    }
    if (target.type === 'handle' && latest.current.tool === 'select') {
      const edge = edgeByKey(target.key);
      const from = edge && latest.current.nodesByKey.get(idKey(edge.from));
      const to = edge && latest.current.nodesByKey.get(idKey(edge.to));
      if (edge && isSelfLoop(edge) && from) {
        gestureRef.current = {
          kind: 'orbit',
          key: target.key,
          start,
          node: from,
          moved: false,
        };
        return;
      }
      gestureRef.current = {
        kind: 'bend',
        key: target.key,
        start,
        from,
        to,
        offset: edge && latest.current.offsetPairs !== false
          ? offsetForPair(edge, latest.current.edges, latest.current.pairOffset)
          : 0,
        moved: false,
      };
      return;
    }
    if (target.type === 'handle') target = { type: 'edge', key: target.key };
    if (target.type === 'node') {
      if (EDGE_TOOLS[latest.current.tool]) {
        gestureRef.current = {
          kind: 'connect', key: target.key, start, moved: false,
        };
      } else {
        const node = latest.current.nodesByKey.get(target.key);
        gestureRef.current = {
          kind: 'node', key: target.key, start, origin: { x: node.x, y: node.y }, moved: false,
        };
      }
    } else {
      gestureRef.current = {
        kind: 'pan', target, start, startTransform: { ...transformRef.current }, moved: false,
      };
    }
  };

  const onPointerMove = (event) => {
    if (pointersRef.current.has(event.pointerId)) {
      pointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
    }
    const gesture = gestureRef.current;

    if (!gesture) {
      const target = findTarget(event.target);
      const next = target.type === 'background' ? null : target;
      setHovered((prev) => (sameTarget(prev, next) ? prev : next));
      return;
    }

    if (gesture.kind === 'pinch') {
      const points = [...pointersRef.current.values()];
      if (points.length < 2) return;
      const rect = getRect();
      const { startTransform: t0 } = gesture;
      const k = clampScale((t0.k * distance(points[0], points[1])) / gesture.startDistance);
      const mid = midpoint(points[0], points[1]);
      const worldX = (gesture.startMid.x - rect.left - t0.x) / t0.k;
      const worldY = (gesture.startMid.y - rect.top - t0.y) / t0.k;
      updateTransform({ k, x: mid.x - rect.left - worldX * k, y: mid.y - rect.top - worldY * k });
      return;
    }

    const dx = event.clientX - gesture.start.x;
    const dy = event.clientY - gesture.start.y;
    if (!gesture.moved && Math.hypot(dx, dy) > CLICK_TOLERANCE) gesture.moved = true;
    if (!gesture.moved) return;

    if (gesture.kind === 'pan') {
      const { startTransform } = gesture;
      updateTransform({ ...startTransform, x: startTransform.x + dx, y: startTransform.y + dy });
    } else if (gesture.kind === 'node') {
      const { k } = transformRef.current;
      const x = gesture.origin.x + dx / k;
      const y = gesture.origin.y + dy / k;
      if (latest.current.physics) {
        if (!gesture.simulating) {
          layout.dragStart(gesture.key);
          gesture.simulating = true;
        }
        layout.drag(gesture.key, x, y);
      } else {
        setLivePositions((prev) => ({ ...prev, [gesture.key]: { x, y } }));
      }
    } else if (gesture.kind === 'connect') {
      const world = toWorld(event.clientX, event.clientY);
      setConnection({ fromKey: gesture.key, x: world.x, y: world.y });
    } else if (gesture.kind === 'bend' && gesture.from && gesture.to) {
      const world = toWorld(event.clientX, event.clientY);
      const roundness = roundnessFromDrag(gesture.from, gesture.to, world, gesture.offset);
      gesture.roundness = roundness;
      setLiveRoundness((prev) => ({ ...prev, [gesture.key]: roundness }));
    } else if (gesture.kind === 'resize' && gesture.origin) {
      const { k } = transformRef.current;
      const next = storedSizeFromDisplay(
        gesture.originShape,
        resizeNode(
          gesture.origin,
          { width: gesture.origin.width, height: gesture.origin.height },
          gesture.dir,
          dx / k,
          dy / k,
        ),
      );
      gesture.size = next;
      setLiveSizes((prev) => ({ ...prev, [gesture.key]: next }));
    } else if (gesture.kind === 'orbit' && gesture.node) {
      const world = toWorld(event.clientX, event.clientY);
      const loopAngle = loopAngleFromDrag(gesture.node, world);
      gesture.loopAngle = loopAngle;
      setLiveLoopAngle((prev) => ({ ...prev, [gesture.key]: loopAngle }));
    }
  };

  const onPointerUp = (event) => {
    pointersRef.current.delete(event.pointerId);
    const gesture = gestureRef.current;
    if (!gesture) return;
    if (gesture.kind === 'pinch') {
      if (pointersRef.current.size < 2) gestureRef.current = null;
      return;
    }
    gestureRef.current = null;
    const world = toWorld(event.clientX, event.clientY);

    if (gesture.kind === 'node') {
      if (!gesture.moved) {
        handleClick({ type: 'node', key: gesture.key }, world);
        return;
      }
      if (latest.current.physics) {
        layout.dragEnd(gesture.key);
        return;
      }
      const node = latest.current.nodesByKey.get(gesture.key);
      const original = nodeByKey(gesture.key);
      if (node && original) {
        const snapping = latest.current.snapToGrid;
        dispatch(updateNode({ id: original.id, x: snap(node.x, snapping), y: snap(node.y, snapping) }));
      }
      setLivePositions((prev) => {
        const next = { ...prev };
        delete next[gesture.key];
        return next;
      });
    } else if (gesture.kind === 'resize') {
      setLiveSizes((prev) => {
        const next = { ...prev };
        delete next[gesture.key];
        return next;
      });
      if (!gesture.moved) {
        handleClick({ type: 'node', key: gesture.key }, world);
        return;
      }
      const original = nodeByKey(gesture.key);
      if (original && gesture.size) {
        const snapping = latest.current.snapToGrid;
        dispatch(updateNode({
          id: original.id,
          x: snap(gesture.size.x, snapping),
          y: snap(gesture.size.y, snapping),
          width: gesture.size.width,
          height: gesture.size.height,
          shape: presentShape(original.shape, DISPLAY_LABEL),
          resized: true,
        }));
      }
    } else if (gesture.kind === 'connect') {
      setConnection(null);
      if (!gesture.moved) {
        createEdge(gesture.key, gesture.key);
        return;
      }
      const target = findTarget(elementAt(event.clientX, event.clientY, event.target));
      if (target.type === 'node') createEdge(gesture.key, target.key);
    } else if (gesture.kind === 'orbit') {
      setLiveLoopAngle((prev) => {
        const next = { ...prev };
        delete next[gesture.key];
        return next;
      });
      if (!gesture.moved) {
        handleClick({ type: 'edge', key: gesture.key }, world);
        return;
      }
      const edge = edgeByKey(gesture.key);
      if (edge && typeof gesture.loopAngle === 'number') {
        dispatch(updateEdge({ id: edge.id, loopAngle: gesture.loopAngle }));
      }
    } else if (gesture.kind === 'bend') {
      setLiveRoundness((prev) => {
        const next = { ...prev };
        delete next[gesture.key];
        return next;
      });
      if (!gesture.moved) {
        handleClick({ type: 'edge', key: gesture.key }, world);
        return;
      }
      const edge = edgeByKey(gesture.key);
      if (edge && typeof gesture.roundness === 'number') {
        const roundness = Math.abs(gesture.roundness) < 0.05 ? 0 : gesture.roundness;
        dispatch(updateEdge({ id: edge.id, roundness }));
      }
    } else if (gesture.kind === 'pan' && !gesture.moved) {
      handleClick(gesture.target, world);
    }
  };

  const onPointerCancel = (event) => {
    pointersRef.current.delete(event.pointerId);
    const gesture = gestureRef.current;
    gestureRef.current = null;
    setConnection(null);
    setLiveRoundness({});
    setLiveLoopAngle({});
    setLiveSizes({});
    if (gesture?.kind === 'node' && gesture.simulating) layout.dragEnd(gesture.key);
  };

  const onDoubleClick = (event) => {
    if (latest.current.tool !== 'select') return;
    // pointer capture retargets the second click to the svg, so look under the pointer instead
    openEditor(findTarget(elementAt(event.clientX, event.clientY, event.target)));
  };

  const onContextMenu = (event) => {
    const target = findTarget(event.target);
    if (target.type === 'background') return;
    event.preventDefault();
    openEditor(target);
  };

  // ---- keyboard ------------------------------------------------------------------------------

  useEffect(() => {
    const handleKeyDown = (event) => {
      const { activeElement } = document;
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(activeElement?.tagName)
        || activeElement?.isContentEditable) return;

      if (event.key === 'Delete' || event.key === 'Backspace') {
        const { highlightedNodes: selNodes, highlightedEdges: selEdges } = latest.current;
        if (selNodes.length > 0) deleteTarget({ type: 'node', key: idKey(selNodes[0]) });
        else if (selEdges.length > 0) deleteTarget({ type: 'edge', key: idKey(selEdges[0]) });
      } else if (event.key === 'Escape') {
        dispatch(clearSelection());
      } else if (event.key === 'f') {
        dispatch(requestFit());
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [dispatch, deleteTarget]);

  // ---- render --------------------------------------------------------------------------------

  const selectedNodeKeys = useMemo(() => new Set(highlightedNodes.map(idKey)), [highlightedNodes]);
  const selectedEdgeKeys = useMemo(() => new Set(highlightedEdges.map(idKey)), [highlightedEdges]);

  return (
    <>
      <EditNodeDialog />
      <EditEdgeDialog />
      <div style={{ width: '100%', height: '100vh', overflow: 'hidden' }}>
        <svg
          id="network-svg"
          ref={svgRef}
          width="100%"
          height="100%"
          style={{
            display: 'block', userSelect: 'none', touchAction: 'none', background: 'white',
          }}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerCancel}
          onPointerLeave={() => setHovered(null)}
          onDoubleClick={onDoubleClick}
          onContextMenu={onContextMenu}
        >
          {snapToGrid && <Grid transform={transform} />}
          <g data-layer="graph" transform={`translate(${transform.x} ${transform.y}) scale(${transform.k})`}>
            <g data-layer="edges">
              {edgeGeometries.map(({ edge, geometry }) => {
                const key = idKey(edge.id);
                const over = (hovered?.type === 'edge' || hovered?.type === 'handle') && hovered.key === key;
                return (
                  <EdgeView
                    key={key}
                    edge={edge}
                    geometry={geometry}
                    selected={selectedEdgeKeys.has(key)}
                    hovered={over}
                    scale={transform.k}
                    showHandle={over
                      || (selectedEdgeKeys.has(key) && highlightedNodes.length === 0)
                      || Object.prototype.hasOwnProperty.call(liveRoundness, key)
                      || Object.prototype.hasOwnProperty.call(liveLoopAngle, key)}
                  />
                );
              })}
            </g>
            {connectionShape && <ConnectionLine geometry={connectionShape} />}
            <g data-layer="nodes">
              {positioned.map((node) => (
                <NodeView
                  key={idKey(node.id)}
                  node={node}
                  size={sizesByKey.get(idKey(node.id))}
                  selected={selectedNodeKeys.has(idKey(node.id))}
                  hovered={(hovered?.type === 'node' || hovered?.type === 'resize')
                    && hovered.key === idKey(node.id)}
                  imageError={Boolean(node.shape === 'image' && imageSizes[node.image]?.error)}
                  scale={transform.k}
                  showResize={!isIdMode(displayMode)
                    && (node.shape === 'ellipse' || node.shape === 'box' || node.shape === 'image')
                    && (selectedNodeKeys.has(idKey(node.id))
                      || ((hovered?.type === 'node' || hovered?.type === 'resize')
                        && hovered.key === idKey(node.id)))}
                />
              ))}
            </g>
          </g>
        </svg>
      </div>
      <Snackbar
        open={saveSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" variant="filled">
          Saving node positions...
        </Alert>
      </Snackbar>
    </>
  );
}
